<template>
    <div>
      <!-- Cabeçalho -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Registros Financeiros</h5>
        <BButton
          v-if="hasPermission('adicionar_registros_financeiros')"
          variant="primary"
          class="d-flex align-items-center"
          @click="irParaCadastro"
        >
          <i class="bi bi-plus-circle-fill mr-2" title="Novo Cadastro"></i>
        </BButton>
      </div>
    
      <!-- Filtros -->
      <div class="d-flex mb-3" style="gap: 10px;">
        <!-- Filtro por Conta Financeira -->
        <input 
          v-model="filters.conta_financeira" 
          placeholder="Filtrar por Conta Financeira"
          class="form-control mr-2"
          @input="filtrarRegistros"
        />
        <!-- Filtro por Mês -->
        <select v-model="filters.mes" @change="filtrarRegistros" class="form-control mr-2">
          <option value="">Selecione o mês</option>
          <option value="1">Janeiro</option>
          <option value="2">Fevereiro</option>
          <option value="3">Março</option>
          <option value="4">Abril</option>
          <option value="5">Maio</option>
          <option value="6">Junho</option>
          <option value="7">Julho</option>
          <option value="8">Agosto</option>
          <option value="9">Setembro</option>
          <option value="10">Outubro</option>
          <option value="11">Novembro</option>
          <option value="12">Dezembro</option>
        </select>
        <!-- Filtro por Ano -->
        <select v-model="filters.ano" @change="filtrarRegistros" class="form-control mr-2">
          <option value="">Selecione o ano</option>
          <option v-for="ano in anos" :key="ano" :value="ano">
            {{ ano }}
          </option>
        </select>
        <BButton 
          variant="secondary" 
          @click="limparFiltros"
          class="d-flex align-items-center justify-content-center"
        >
          <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
        </BButton>
      </div>
    
      <!-- Tabela -->
      <BTable 
        :items="items" 
        :fields="fields" 
        striped 
        hover
      >
        <!-- Cabeçalho customizado para ordenação -->
        <template #head(conta_financeira)>
          <span @click="atualizarOrdenacao('conta_financeira')" style="cursor: pointer;">
            Conta Financeira
            <i v-if="currentSort.key === 'conta_financeira'" :class="currentSort.order === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </span>
        </template>
        <template #head(data_formatada)>
          <span @click="atualizarOrdenacao('data')" style="cursor: pointer;">
            Data do saldo
            <i v-if="currentSort.key === 'data'" :class="currentSort.order === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </span>
        </template>
        <template #head(saldo_formatado)>
          <span @click="atualizarOrdenacao('saldo')" style="cursor: pointer;">
            Saldo consolidado
            <i v-if="currentSort.key === 'saldo'" :class="currentSort.order === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </span>
        </template>
    
        <!-- Coluna: Conta Financeira concatenada -->
        <template #cell(conta_financeira)="row">
          <template v-if="getConta(row.item.conta_financeira_id)">
            {{ getConta(row.item.conta_financeira_id).apelido }} - 
            {{ getConta(row.item.conta_financeira_id).agencia }} - 
            {{ getConta(row.item.conta_financeira_id).numero_conta }}
          </template>
          <template v-else>
            - - 
          </template>
        </template>
        <!-- Coluna: Data formatada -->
        <template #cell(data_formatada)="row">
          {{ globalUtils.formatarData(row.item.data) }}
        </template>
        <!-- Coluna: Saldo formatado -->
        <template #cell(saldo_formatado)="row">
          {{ globalUtils.formatarValor(row.item.saldo) }}
        </template>
        <!-- Coluna Ações -->
        <template #cell(actions)="row">
          <i
            v-if="hasPermission('detalhar_registros_financeiros')"
            class="bi bi-eye-fill text-primary mx-2"
            title="Detalhar"
            @click="detalharRegistro(row.item.id)"
          ></i>
          <i
            v-if="hasPermission('alterar_registros_financeiros')"
            class="bi bi-pencil-fill text-warning mx-2"
            title="Editar"
            @click="editarRegistro(row.item.id)"
          ></i>
          <i
            v-if="hasPermission('excluir_registros_financeiros')"
            class="bi bi-trash-fill text-danger mx-2"
            title="Excluir"
            @click="excluirRegistro(row.item.id)"
          ></i>
        </template>
      </BTable>
    
      <!-- Paginação -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <!-- 1. Resumo -->
        <div>
          Mostrando {{ startRow }} a {{ endRow }} de {{ totalRegistros }} registros
        </div>

        <!-- 2. Itens por página -->
        <div class="d-flex align-items-center" style="gap: .5rem;">
          <b-form-select
            v-model="limitePorPagina"
            :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
            size="sm"
            style="width: 6rem"
          />
        </div>

        <!-- 3. Paginação -->
        <div class="d-flex align-items-center">
          <b-pagination
            v-model="paginaAtual"
            :per-page="limitePorPagina"
            :total-rows="totalRegistros"
            size="sm"
            limit="5"
          />
        </div>
      </div>
    </div>
  </template>
    
  <script setup>
  import { ref, reactive, onMounted, computed, watch } from 'vue';
  import axios from '@/services/axios';
  import { BButton, BTable, BFormSelect, BPagination } from 'bootstrap-vue-next';
  import globalUtils from '@/utils/globalUtils';
  import { useRouter } from 'vue-router';
  import { usePermissions } from "@/composables/usePermissions";

    
  const registros = ref([]);
    
  const fields = ref([
    { key: 'id', label: '#' },
    { key: 'conta_financeira', label: 'Conta Financeira' }, // remove o sortable daqui
    { key: 'data_formatada', label: 'Data do saldo' },
    { key: 'saldo_formatado', label: 'Saldo consolidado' },
    { key: 'actions', label: 'Ações', class: 'text-end' },
  ]);
    
  const filters = reactive({
    conta_financeira: '',
    mes: '',
    ano: ''
  });
    
  const items = computed(() => {
    return Array.isArray(registros.value)
        ? JSON.parse(JSON.stringify(registros.value))
        : [];
  });
    
  const paginaAtual = ref(1);
  const limitePorPagina = ref(10);
  const totalPaginas = ref(1);
  const totalRegistros  = ref(0);
  const currentSort = reactive({ key: 'data', order: 'desc' });
  const router = useRouter();
    
  // Importa o composable de permissões
  const { fetchPermissions, hasPermission } = usePermissions();

  async function carregarRegistros() {
    try {
        const params = {
          page: paginaAtual.value,
          limit: limitePorPagina.value,
          conta_financeira: filters.conta_financeira || undefined,
          mes: filters.mes || undefined,
          ano: filters.ano || undefined,
          sort: currentSort.key,
          order: currentSort.order,
        };
        console.log("Carregar Registros - Params:", params);
        const response = await axios.get(`/financeiro/registros-financeiros`, { params });
        console.log("Response data:", response.data);
        registros.value = Array.isArray(response.data.data) ? response.data.data : [];
        totalPaginas.value = response.data.totalPages;
        totalRegistros.value  = response.data.total || registros.value.length;
    } catch (error) {
        console.error("Erro ao carregar registros financeiros:", error);
    }
  }

 
  // opções de itens por página
  const pageOptions = [10, 15, 20, 25, 50];

  // computeds para o resumo
  const startRow = computed(() =>
    (paginaAtual.value - 1) * limitePorPagina.value + 1
  );
  const endRow = computed(() =>
    Math.min(paginaAtual.value * limitePorPagina.value, totalRegistros.value)
  );


  const contas = ref([]);
  async function carregarContas() {
    try {
        const response = await axios.get(`/financeiro/contas-financeiras`);
        contas.value = response.data.data ? response.data.data : response.data;
    } catch (error) {
        console.error("Erro ao carregar contas financeiras:", error);
    }
  }
    
  function getConta(id) {
    return contas.value.find(c => c.id === id);
  }
    
  function filtrarRegistros() {
    paginaAtual.value = 1;
    carregarRegistros();
  }
    
  function irParaCadastro() {
    router.push("/financeiro/registros-financeiros/novo");
  }
    
  function detalharRegistro(id) {
    router.push(`/financeiro/registros-financeiros/${id}/detalhar`);
  }
    
  function editarRegistro(id) {
    router.push(`/financeiro/registros-financeiros/${id}`);
  }
    
  async function excluirRegistro(id) {
    if (confirm("Tem certeza que deseja excluir este registro financeiro?")) {
      try {
        await axios.delete(`/financeiro/registros-financeiros/${id}`);
        carregarRegistros();
      } catch (error) {
        console.error("Erro ao excluir registro financeiro:", error);
      }
    }
  }
    
  function atualizarOrdenacao(key) {
    if (currentSort.key === key) {
      currentSort.order = currentSort.order === 'asc' ? 'desc' : 'asc';
    } else {
      currentSort.key = key;
      currentSort.order = 'asc';
    }
    console.log("Atualizando ordenação:", currentSort);
    carregarRegistros();
  }
   
    
  function limparFiltros() {
    filters.conta_financeira = "";
    filters.mes = "";
    filters.ano = "";
    paginaAtual.value = 1;
    carregarRegistros();
  }
    
  const anos = computed(() => {
    const currentYear = new Date().getFullYear();
    const lista = [];
    for (let i = 0; i < 15; i++) {
        lista.push(currentYear - i);
    }
    return lista;
  });
    
  onMounted(async () => {
    await fetchPermissions();
    await carregarContas();
    carregarRegistros();
  });

  // watch para recarregar ao mudar página
  watch(paginaAtual, () => carregarRegistros());

  // watch para recarregar ao mudar limite
  watch(limitePorPagina, (nv, ov) => {
    if (nv !== ov) {
      paginaAtual.value = 1;
      carregarRegistros();
    }
  });

  </script>
  
<style scoped>

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>
